import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import {
  adminListPartnerBrands, adminCreatePartnerBrand,
  adminUpdatePartnerBrand, adminDeletePartnerBrand,
  adminUploadImage,
} from "@/lib/api";
import AdminHeader from "@/components/admin/AdminHeader";

const assetUrl = (u) => (!u ? "" : u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

const emptyForm = {
  id: null, name: "", logo: null, display_order: 100, status: "draft",
};

export default function AdminPartnerBrandsPage() {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoadingItems(true);
    try { setItems(await adminListPartnerBrands()); }
    catch { toast.error("Failed to load brands"); }
    setLoadingItems(false);
  }, []);

  useEffect(() => {
    if (!loading && !user) navigate("/admin/login");
    else if (user) load();
  }, [user, loading, navigate, load]);

  const startCreate = () => {
    const maxOrder = items.reduce((m, x) => Math.max(m, x.display_order || 0), 0);
    setEditing({ ...emptyForm, display_order: maxOrder + 10 });
  };
  const startEdit = (b) => setEditing({ ...emptyForm, ...b });
  const cancel = () => setEditing(null);

  const doSave = async (statusOverride) => {
    if (!editing.name.trim()) { toast.error("Brand name is required"); return; }
    const payload = {
      name: editing.name.trim(),
      logo: editing.logo || null,
      display_order: Number(editing.display_order) || 100,
      status: statusOverride || editing.status || "draft",
    };
    setSaving(true);
    try {
      if (editing.id) await adminUpdatePartnerBrand(editing.id, payload);
      else await adminCreatePartnerBrand(payload);
      toast.success(editing.id ? "Brand updated" : "Brand added");
      setEditing(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const onDelete = async (b) => {
    if (!window.confirm(`Delete brand "${b.name}"?`)) return;
    try { await adminDeletePartnerBrand(b.id); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const move = async (brand, delta) => {
    const idx = items.findIndex((x) => x.id === brand.id);
    const target = items[idx + delta];
    if (!target) return;
    try {
      await Promise.all([
        adminUpdatePartnerBrand(brand.id, {
          name: brand.name, logo: brand.logo || null,
          display_order: target.display_order, status: brand.status,
        }),
        adminUpdatePartnerBrand(target.id, {
          name: target.name, logo: target.logo || null,
          display_order: brand.display_order, status: target.status,
        }),
      ]);
      load();
    } catch { toast.error("Reorder failed"); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    try {
      const { url } = await adminUploadImage(file);
      setEditing((prev) => ({ ...prev, logo: url }));
    } catch { toast.error("Upload failed"); }
  };

  if (loading || !user) return <div className="admin-shell"><div className="admin-loading">Loading…</div></div>;

  return (
    <div className="admin-shell utsah-app" data-testid="admin-pbrands-page">
      <AdminHeader active="partner-brands" onLogout={async () => { await logout(); navigate("/admin/login"); }} email={user.email} />
      <div className="admin-container">
        <div className="admin-heading-row">
          <div>
            <div className="eyebrow">Partner Benefits</div>
            <h1 className="admin-heading">Partner Brands</h1>
          </div>
          {!editing && (
            <button className="admin-export-btn" onClick={startCreate} data-testid="admin-new-pbrand-btn">+ New Brand</button>
          )}
        </div>

        {editing ? (
          <BrandForm form={editing} saving={saving}
            onChange={setEditing} onSave={doSave} onCancel={cancel} onUploadLogo={uploadLogo} />
        ) : (
          <div className="admin-table-wrap" data-testid="admin-pbrands-table">
            {loadingItems ? (
              <div className="admin-loading">Loading…</div>
            ) : items.length === 0 ? (
              <div className="admin-empty">No brands yet. Click "New Brand" to add one.</div>
            ) : (
              <table className="admin-table">
                <thead>
                  <tr><th>Order</th><th>Logo</th><th>Name</th><th>Status</th><th className="admin-th-actions">Actions</th></tr>
                </thead>
                <tbody>
                  {items.map((b, i) => (
                    <tr key={b.id} data-testid={`pbrand-row-${b.id}`}>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <span style={{ minWidth: 32, fontWeight: 700 }}>{b.display_order}</span>
                          <button className="admin-link-btn" onClick={() => move(b, -1)} disabled={i === 0}>↑</button>
                          <button className="admin-link-btn" onClick={() => move(b, 1)} disabled={i === items.length - 1}>↓</button>
                        </div>
                      </td>
                      <td>
                        {b.logo ? (
                          <img src={assetUrl(b.logo)} alt={b.name}
                            style={{ maxHeight: 32, maxWidth: 100, objectFit: "contain", display: "block" }} />
                        ) : (<span style={{ opacity: 0.4 }}>—</span>)}
                      </td>
                      <td><strong>{b.name}</strong></td>
                      <td><span className={`admin-badge admin-badge-${b.status}`}>{b.status}</span></td>
                      <td className="admin-td-actions">
                        <button className="admin-link-btn" onClick={() => startEdit(b)} data-testid={`pbrand-edit-${b.id}`}>Edit</button>
                        <button className="admin-link-btn admin-link-danger" onClick={() => onDelete(b)} data-testid={`pbrand-del-${b.id}`}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
      <Toaster position="bottom-center" richColors theme="light" />
    </div>
  );
}

function BrandForm({ form, saving, onChange, onSave, onCancel, onUploadLogo }) {
  const set = (k, v) => onChange({ ...form, [k]: v });
  return (
    <div className="admin-form" data-testid="admin-pbrand-form">
      <section className="admin-form-section">
        <h3 className="admin-form-section-title">1 · Brand Details</h3>
        <div className="admin-form-row">
          <label>Brand Name<span className="req">*</span></label>
          <input type="text" value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="e.g. Amazon"
            data-testid="pbrand-form-name" />
          <div className="admin-form-hint">Used as alt text on the logo image.</div>
        </div>
      </section>

      <section className="admin-form-section">
        <h3 className="admin-form-section-title">2 · Brand Logo</h3>
        <div className="admin-form-row">
          <label>Upload Logo (SVG preferred; PNG or WebP also supported)</label>
          <input type="file" accept=".svg,.png,.webp,.jpg,.jpeg,image/*"
            onChange={(e) => onUploadLogo(e.target.files?.[0])}
            data-testid="pbrand-form-logo-upload" />
          {form.logo && (
            <div className="admin-image-tile" style={{ maxWidth: 240, marginTop: 12, background: "#FDF6EE", padding: 12 }}>
              <img src={assetUrl(form.logo)} alt=""
                style={{ maxHeight: 40, maxWidth: "100%", objectFit: "contain", display: "block", margin: "0 auto" }} />
              <div className="admin-image-controls">
                <button type="button" className="admin-link-danger" onClick={() => set("logo", null)}>Remove</button>
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="admin-form-section">
        <h3 className="admin-form-section-title">3 · Display & Publishing</h3>
        <div className="admin-form-row">
          <label>Display Order (lower first)</label>
          <input type="number" min={0} value={form.display_order}
            onChange={(e) => set("display_order", e.target.value)}
            data-testid="pbrand-form-order" />
        </div>
        <div className="admin-form-row">
          <label>Status</label>
          <select value={form.status}
            onChange={(e) => set("status", e.target.value)}
            data-testid="pbrand-form-status">
            <option value="draft">Draft (hidden)</option>
            <option value="published">Published (live)</option>
          </select>
        </div>
      </section>

      <div className="admin-form-actions" data-testid="pbrand-form-actions">
        <button className="admin-nav-btn" onClick={onCancel} disabled={saving} data-testid="pbrand-form-cancel">Cancel</button>
        <button className="admin-nav-btn" onClick={() => onSave("draft")} disabled={saving} data-testid="pbrand-form-save-draft">Save Draft</button>
        <button className="admin-export-btn" onClick={() => onSave("published")} disabled={saving} data-testid="pbrand-form-publish">
          {saving ? "Saving…" : "Publish"}
        </button>
      </div>
    </div>
  );
}
